import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { customerData } from '../../../utilities/CustomerData'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'

export default function DashboardReauthFlow() {
  const navigate = useNavigate()
  const [redoSteps, setRedoSteps] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchStatus = async () => {
    try {
      // Fetch redo steps
      const redoRes = await fetch(`${apiURL}/api/getReauthRedoSteps?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
        headers: { 'Authorization': customerData.accessToken }
      })
      const redoData = await redoRes.json()
      
      if (redoData.response?.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken) return fetchStatus()
      }
      
      setRedoSteps(redoData.response)

      // Fetch history for the latest rejection note
      const historyRes = await fetch(`${apiURL}/api/getReauthRejectionHistory?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
        headers: { 'Authorization': customerData.accessToken }
      })
      const historyData = await historyRes.json()
      setHistory(historyData.response || [])
    } catch (err) {
      console.error('Failed to fetch reauth status:', err)
      setError('Failed to load verification status.')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  if (loading) {
    return (
      <div className='w-full p-8 flex justify-center'>
        <div className='flex items-center gap-3'>
          <div className='loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6'></div>
          <p className='text-gray-500 font-semibold'>Updating status...</p>
        </div>
      </div>
    )
  }

  const latestRejection = history && history.length > 0 ? history[0] : null

  return (
    <div className='w-full flex flex-col gap-4 mt-2'>
      {latestRejection && (
        <div className='bg-red-50 border border-red-200 p-5 rounded-xl shadow-sm'>
          <div className='flex gap-3'>
            <div className='flex-shrink-0 mt-0.5'>
              <svg className='h-5 w-5 text-red-500' viewBox='0 0 20 20' fill='currentColor'>
                <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
              </svg>
            </div>
            <div className='flex-1'>
              <h3 className='text-sm font-bold text-red-800 uppercase tracking-tight'>Note from Processor</h3>
              <p className='mt-1 text-sm text-red-700 leading-relaxed font-medium'>
                {latestRejection.rejectionNote || 'Please review the checklist below for required updates.'}
              </p>
              <p className='mt-2 text-[10px] text-red-400 font-bold uppercase'>
                Rejected on {new Date(latestRejection.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className='shadow-md p-6 rounded-xl w-full flex flex-col bg-white font-mont gap-6 border border-gray-100'>
        <div className='flex items-center justify-between'>
          <p className='font-bold text-xl text-[#646464]'>Verification Checklist</p>
          <span className='text-[10px] font-black text-blue-500 bg-blue-50 px-2 py-1 rounded uppercase'>Revision Required</span>
        </div>
        
        <div className='flex flex-col gap-3'>
          <Step 
            number={1} 
            label="ID & Selfie Verification" 
            isRedo={redoSteps?.redoStep1} 
            isCompleted={!redoSteps?.redoStep1 && !!redoSteps?.step1Completed}
          />
          <Step 
            number={2} 
            label="Verification Document" 
            isRedo={redoSteps?.redoStep2}
            isCompleted={!redoSteps?.redoStep2 && !!redoSteps?.step2Completed}
          />
          <Step 
            number={3} 
            label="Signed 8821 Form" 
            isRedo={redoSteps?.redoStep3}
            isCompleted={!redoSteps?.redoStep3 && !!redoSteps?.step3Completed}
          />
        </div>

        <div className='mt-2'>
          <button 
            onClick={() => {
              const step1Incomplete = redoSteps?.redoStep1 || !redoSteps?.step1Completed
              const step2Incomplete = redoSteps?.redoStep2 || !redoSteps?.step2Completed
              
              if (step1Incomplete) {
                navigate('/reauth/id-verification', { state: { redoSteps } })
              } else if (step2Incomplete) {
                navigate('/reauth/verification-doc', { state: { redoSteps } })
              } else {
                navigate('/reauth/sign-8821', { state: { redoSteps } })
              }
            }}
            className='w-full px-6 py-4 bg-[#5186C0] text-white font-bold rounded-xl shadow-lg hover:bg-[#4070A0] hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-center flex items-center justify-center gap-2'
          >
            {latestRejection ? 'Restart Verification Flow' : 'Begin Verification Flow'}
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6' /></svg>
          </button>
          <p className='text-center text-[11px] text-gray-400 mt-4 leading-relaxed'>
            We require these additional steps to ensure the security of your account and verify your identity with the IRS.
          </p>
        </div>
      </div>
    </div>
  )
}

function Step({ number, label, isRedo, isCompleted }) {
  return (
    <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isRedo ? 'bg-red-50 border-red-100 shadow-inner' : 'bg-gray-50 border-gray-100'}`}>
      <div className='flex items-center gap-4'>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-base shadow-sm ${isRedo ? 'bg-white text-red-600 border-2 border-red-500' : isCompleted ? 'bg-green-500 text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>
          {isCompleted ? (
            <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M5 13l4 4L19 7' /></svg>
          ) : number}
        </div>
        <div>
          <p className={`font-bold text-sm ${isRedo ? 'text-red-800' : isCompleted ? 'text-gray-500' : 'text-[#646464]'}`}>{label}</p>
          {isRedo ? (
            <p className='text-[10px] font-black text-red-500 uppercase tracking-widest mt-0.5'>Update Required</p>
          ) : isCompleted ? (
            <p className='text-[10px] font-bold text-green-600 uppercase tracking-widest mt-0.5'>Verified</p>
          ) : (
             <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5'>Pending Task</p>
          )}
        </div>
      </div>
      <div className='flex items-center'>
        {isCompleted ? (
          <div className='bg-green-100 p-1.5 rounded-full'>
            <svg className='w-4 h-4 text-green-600' fill='currentColor' viewBox='0 0 20 20'><path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' /></svg>
          </div>
        ) : isRedo ? (
          <div className='bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-lg uppercase shadow-sm'>Redo</div>
        ) : (
          <div className='bg-gray-200 text-gray-500 text-[9px] font-black px-2 py-1 rounded-lg uppercase'>Waiting</div>
        )}
      </div>
    </div>
  )
}
